import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';

import { createBlobFromResponse } from '@helpers/fileHelpers';

import {
  generateInvoicePdfApi as generateAppInvoicePdfApi,
  getInvoiceDetailsApi as getAppInvoiceDetailsApi,
} from '@api/billing-app.api';
import {
  generateInvoicePdfApi as generateConciergeInvoicePdfApi,
  getInvoiceDetailsApi as getConciergeInvoiceDetailsApi,
} from '@api/billing-concierge.api';

import { getCurrentWorkspaceId } from '@redux/selectors/workspaces.selectors';
import { fetchAppBillingDetails } from '@redux/thunks/billings-app.thunks';

import Loader from '@uikit/old/Loader/Loader';
import PageHeader from '@uikit/old/PageHeader/PageHeader';

import BackArrowButton from '@components/BackArrowButton/BackArrowButton';
import InvoiceInfo from '@components/InvoiceInfo/InvoiceInfo';
import urls from '@constants/urls';
import { Button } from '@uikit/Form/Button/Button';
import { isConciergeRealm } from '@utils/realmIdentifier';
import InvoiceConciergeInfo from '@components/Concierge/Settings/InvoiceInfo/InvoiceConciergeInfo';
import { InvoiceType } from '@ts/billing-app.types';
import { InvoiceConciergeDetailsType } from '@ts/billing-concierge.types';

function InvoiceDetails({
  match: {
    params: { invoiceId },
  },
}): JSX.Element {
  const dispatch = useDispatch();
  const curWorkspaceId = useSelector(getCurrentWorkspaceId);

  const [isInvoiceLoading, setInvoiceLoading] = useState(false);
  const getInvoiceDetailsCall = isConciergeRealm()
    ? getConciergeInvoiceDetailsApi
    : getAppInvoiceDetailsApi;
  const generateInvoicePdfCall = isConciergeRealm()
    ? generateConciergeInvoicePdfApi
    : generateAppInvoicePdfApi;

  const { data: invoiceDetails, isLoading } = useQuery({
    queryKey: ['invoice-details', curWorkspaceId, invoiceId],
    queryFn: () => getInvoiceDetailsCall(curWorkspaceId, Number(invoiceId)),
    refetchOnWindowFocus: false,
  });

  const handlePaymentSuccess = () => {
    dispatch(fetchAppBillingDetails());
  };

  const handleDownloadInvoice = () => {
    setInvoiceLoading(true);
    generateInvoicePdfCall(invoiceDetails.id, curWorkspaceId)
      .then((res) => {
        const blob = createBlobFromResponse(res);

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = res.title;
        link.click();
        link.remove();
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setInvoiceLoading(false));
  };

  if (isLoading) {
    return <Loader isLoading withTopMargin />;
  }

  return (
    <div className="invoice-details">
      <PageHeader
        renderHeaderTitle={() => (
          <div className="invoice-details__header-title">
            <BackArrowButton forceLink={urls.SETTINGS_BILLING} />
            Invoice
          </div>
        )}
        renderHeaderActions={() => (
          <Button isLoading={isInvoiceLoading} onClick={handleDownloadInvoice}>
            Download
          </Button>
        )}
      />
      {isConciergeRealm() ? (
        <InvoiceConciergeInfo
          invoice={invoiceDetails as InvoiceConciergeDetailsType}
          onPaymentSuccess={handlePaymentSuccess}
        />
      ) : (
        <InvoiceInfo
          invoice={invoiceDetails as InvoiceType}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
}

export default InvoiceDetails;
